import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, articles } from '@/mocks/clCommunityData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'tech-discussion');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug);
  const filteredArticles = articles.filter((a) => a.category === activeSlug);
  const totalPages = Math.ceil(filteredArticles.length / pageSize);
  const paginatedArticles = filteredArticles.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  useEffect(() => {
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSlug]);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <section className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: activeCategory?.name || '分类' },
              ]}
            />
            <div className="mt-4">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                {activeCategory?.name}
              </h1>
              <p className="text-sm text-foreground-500 mt-1">
                {activeCategory?.description} · {filteredArticles.length} 篇文章
              </p>
            </div>

            <div className="flex items-center gap-2 mt-6 pb-2 overflow-x-auto flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => { setActiveSlug(cat.slug); setCurrentPage(1); }}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-50 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-12">
          {paginatedArticles.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {paginatedArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden group cursor-pointer hover:-translate-y-0.5 transition-all duration-300"
                  >
                    <div className="relative h-44 md:h-48 overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        title={`${article.title} - CL社区`}
                      />
                      {article.isPinned && (
                        <span className="absolute top-3 left-3 bg-primary-500 text-white text-xs font-medium px-2.5 py-1 rounded-full">
                          置顶
                        </span>
                      )}
                    </div>
                    <div className="p-4 md:p-5">
                      <h2 className="font-heading font-semibold text-foreground-900 text-base md:text-lg mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-4">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <img
                            src={article.author.avatar}
                            alt={article.author.name}
                            className="w-6 h-6 rounded-full object-cover"
                          />
                          <span className="text-xs text-foreground-600">{article.author.name}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-foreground-500">
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i>
                            {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-chat-3-line"></i>
                            {article.comments}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-10">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="w-9 h-9 rounded-lg flex items-center justify-center border border-background-200 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`w-9 h-9 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                        currentPage === i + 1
                          ? 'bg-primary-500 text-white'
                          : 'border border-background-200 text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="w-9 h-9 rounded-lg flex items-center justify-center border border-background-200 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类下暂无文章</p>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
}